import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CircularProgress from '@mui/material/CircularProgress';
import { useSavedItems } from '../../../contexts/SavedItemsContext';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { EstimatedCosts } from '../../provider';
import SaveToModal from '../../modals/SaveToModal/SaveToModal';
import CreateSavedListModal from '../../modals/CreateSavedListModal/CreateSavedListModal';
import { medicationService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './MedicationDetail.module.css';

interface Medication {
  id: string;
  ndc: string;
  name: string;
  genericName: string;
  brandNames: string[];
  manufacturer: string;
  category: string;
  description: string;
  commonUses: string[];
  dosageForms: {
    form: string;
    strengths: string[];
  }[];
  sideEffects: {
    common: string[];
    serious: string[];
  };
  interactions: string[];
  warnings: string[];
  howToTake: string;
  missedDose: string;
  storage: string;
  pricing: {
    retail: number;
    withInsurance: number;
    generic: number;
  };
  alternatives: {
    id: string;
    name: string;
    price: number;
  }[];
  pharmacies: {
    name: string;
    distance: string;
    price: number;
    inStock: boolean;
  }[];
}

const MedicationDetail: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isItemSaved } = useSavedItems();
  const [medication, setMedication] = useState<Medication | null>(null);
  const [selectedStrength, setSelectedStrength] = useState<string>('');
  const [selectedQuantity, setSelectedQuantity] = useState<number>(30);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  
  const { loading, error, execute: fetchMedication } = useApiCall(() =>
    medicationService.getMedicationDetail(id || '')
  );

  useEffect(() => {
    const loadMedication = async () => {
      if (id) {
        const result = await fetchMedication();
        if (result?.success && result.data) {
          setMedication(result.data);
          // Set default strength if available
          if (result.data.dosageForms?.[0]?.strengths?.[2]) {
            setSelectedStrength(result.data.dosageForms[0].strengths[2]);
          } else if (result.data.dosageForms?.[0]?.strengths?.[0]) {
            setSelectedStrength(result.data.dosageForms[0].strengths[0]);
          }
        }
      }
    };

    loadMedication();
  }, [id]);

  const isSaved = medication ? isItemSaved(medication.id, 'medication') : false;

  const handleSave = () => {
    if (!medication) return;
    setShowSaveModal(true);
  };

  const handleShare = async () => {
    if (navigator.share && medication) {
      try {
        await navigator.share({
          title: medication.name,
          text: `Check out ${medication.name} on Mario Health`,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled the share dialog or an error occurred
        // Silently handle the error - no need to show anything to the user
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (!navigator.share) {
      // Fallback for browsers that don't support native share
      console.log('Web Share API not supported');
      // Could implement a custom share modal here
    }
  };

  const handlePharmacyClick = (pharmacy: any) => {
    // Navigate to pharmacy or show directions
    console.log('Navigate to pharmacy:', pharmacy);
  };

  if (loading) {
    return (
      <div className={styles.medicationDetail}>
        <div className={styles.loading}>
          <CircularProgress size={40} />
          <p style={{ marginTop: '16px' }}>Loading medication details...</p>
        </div>
      </div>
    );
  }

  if (error || !medication) {
    return (
      <div className={styles.medicationDetail}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </button>
          <h1 className={styles.headerTitle}>Medication Details</h1>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <p>{error ? 'Unable to load medication details.' : 'Medication not found.'}</p>
          <button 
            onClick={() => navigate(-1)} 
            style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate savings percentage
  const savingsPercentage = Math.round(((medication.pricing.retail - medication.pricing.withInsurance) / medication.pricing.retail) * 100);
  
  const estimatedCosts = [
    {
      label: `${selectedStrength} - ${selectedQuantity} tablets`,
      billedAmount: `$${(medication.pricing.retail * selectedQuantity / 30).toFixed(2)}`,
      insurancePays: `$${((medication.pricing.retail - medication.pricing.withInsurance) * selectedQuantity / 30).toFixed(2)}`,
      patientCost: `$${(medication.pricing.withInsurance * selectedQuantity / 30).toFixed(2)}`,
      savingsPercentage: savingsPercentage
    }
  ];

  return (
    <div className={styles.medicationDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerActions}>
          <button className={styles.actionButton} onClick={handleSave}>
            {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
          </button>
          <button className={styles.actionButton} onClick={handleShare}>
            <ShareIcon />
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.medicationHeader}>
          <MedicationIcon className={styles.medicationIcon} />
          <div className={styles.medicationInfo}>
            <h1 className={styles.medicationName}>{medication.name}</h1>
            <p className={styles.genericName}>Generic: {medication.genericName}</p>
            <p className={styles.category}>{medication.category}</p>
          </div>
        </div>

        <div className={styles.brandNames}>
          <span className={styles.brandLabel}>Brand names:</span>
          {medication.brandNames.map((brand, index) => (
            <span key={index} className={styles.brandChip}>{brand}</span>
          ))}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>What is {medication.name}?</h2>
          <p className={styles.description}>{medication.description}</p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Common Uses</h2>
          <ul className={styles.usesList}>
            {medication.commonUses.map((use, index) => (
              <li key={index}>{use}</li>
            ))}
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Dosage & Pricing</h2>
          
          <div className={styles.dosageSelector}>
            <div className={styles.selectorGroup}>
              <label>Strength</label>
              <select 
                value={selectedStrength} 
                onChange={(e) => setSelectedStrength(e.target.value)}
                className={styles.selector}
              >
                {medication.dosageForms[0].strengths.map((strength) => (
                  <option key={strength} value={strength}>{strength}</option>
                ))}
              </select>
            </div>
            
            <div className={styles.selectorGroup}>
              <label>Quantity</label>
              <select 
                value={selectedQuantity} 
                onChange={(e) => setSelectedQuantity(Number(e.target.value))}
                className={styles.selector}
              >
                <option value={30}>30 tablets</option>
                <option value={60}>60 tablets</option>
                <option value={90}>90 tablets</option>
              </select>
            </div>
          </div>

          <EstimatedCosts
            insurancePlan="Your Insurance"
            costs={estimatedCosts}
            dataSourceNote="Prices based on your insurance coverage and pharmacy benefits"
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Available at Pharmacies</h2>
          <div className={styles.pharmacyList}>
            {medication.pharmacies.map((pharmacy, index) => (
              <div 
                key={index} 
                className={styles.pharmacyCard}
                onClick={() => handlePharmacyClick(pharmacy)}
              >
                <div className={styles.pharmacyHeader}>
                  <LocalPharmacyIcon className={styles.pharmacyIcon} />
                  <div className={styles.pharmacyInfo}>
                    <h3 className={styles.pharmacyName}>{pharmacy.name}</h3>
                    <div className={styles.pharmacyMeta}>
                      <LocationOnIcon className={styles.smallIcon} />
                      <span>{pharmacy.distance}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.pharmacyPrice}>
                  <span className={styles.priceLabel}>Your price</span>
                  <span className={styles.priceValue}>${pharmacy.price}</span>
                </div>
                <div className={styles.stockStatus}>
                  {pharmacy.inStock ? (
                    <span className={styles.inStock}>In Stock</span>
                  ) : (
                    <span className={styles.outOfStock}>Out of Stock</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>How to Take</h2>
          <p className={styles.instructions}>{medication.howToTake}</p>
          
          <div className={styles.infoBox}>
            <InfoOutlinedIcon className={styles.infoIcon} />
            <div>
              <h4>Missed Dose</h4>
              <p>{medication.missedDose}</p>
            </div>
          </div>
          
          <div className={styles.infoBox}>
            <InfoOutlinedIcon className={styles.infoIcon} />
            <div>
              <h4>Storage</h4>
              <p>{medication.storage}</p>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Side Effects</h2>
          
          <div className={styles.sideEffectsGroup}>
            <h3 className={styles.subTitle}>Common Side Effects</h3>
            <ul className={styles.sideEffectsList}>
              {medication.sideEffects.common.map((effect, index) => (
                <li key={index}>{effect}</li>
              ))}
            </ul>
          </div>
          
          <div className={styles.warningBox}>
            <WarningAmberIcon className={styles.warningIcon} />
            <div>
              <h3 className={styles.subTitle}>Serious Side Effects</h3>
              <p className={styles.warningText}>
                Seek medical attention immediately if you experience:
              </p>
              <ul className={styles.seriousList}>
                {medication.sideEffects.serious.map((effect, index) => (
                  <li key={index}>{effect}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Drug Interactions</h2>
          <p className={styles.interactionIntro}>
            This medication may interact with:
          </p>
          <ul className={styles.interactionsList}>
            {medication.interactions.map((interaction, index) => (
              <li key={index}>{interaction}</li>
            ))}
          </ul>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Important Warnings</h2>
          <div className={styles.warningsList}>
            {medication.warnings.map((warning, index) => (
              <div key={index} className={styles.warningItem}>
                <WarningAmberIcon className={styles.smallWarningIcon} />
                <span>{warning}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Alternative Medications</h2>
          <div className={styles.alternativesList}>
            {medication.alternatives.map((alt) => (
              <div 
                key={alt.id} 
                className={styles.alternativeCard}
                onClick={() => navigate(`/medication/${alt.id}`)}
              >
                <MedicationIcon className={styles.altIcon} />
                <div className={styles.altInfo}>
                  <h4>{alt.name}</h4>
                  <span className={styles.altPrice}>From ${alt.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.bottomActions}>
        <button className={styles.primaryButton}>
          <AttachMoneyIcon />
          Compare Prices
        </button>
        <button className={styles.secondaryButton}>
          <LocalPharmacyIcon />
          Find Pharmacy
        </button>
      </div>

      {medication && (
        <SaveToModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          item={{
            id: medication.id,
            type: 'medication',
            title: medication.name,
            subtitle: `${medication.genericName} • ${medication.category}`,
            data: medication
          }}
          onCreateNewList={() => {
            setShowSaveModal(false);
            setShowCreateListModal(true);
          }}
        />
      )}

      <CreateSavedListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onListCreated={(listId) => {
          navigate(`/list/${listId}`);
        }}
      />
    </div>
  );
};

export default MedicationDetail;